import { h, render, isVNode } from 'vue'
import Msg from './src/msg.vue'
import { SFCWithInstall, withInstall } from '@touchvue/utils'
import { generateUUID, removeElement } from '@touchvue/utils'

const ToMsg: SFCWithInstall<typeof Msg> = withInstall(Msg)
export * from './src/instance'

// 定义选项接口
export interface MsgOptions {
	title?: string
	content?: string
	pic?: string
	picPosition?: string
	position?: string
	height?: string | number
	confirmButtonText?: string
	cancelButtonText?: string
	global?: boolean
	show?: boolean
	[key: string]: any
}

// 创建msg实例的函数
function createMsgInstance(app: any, ...args: any[]): Promise<boolean> {
	if (args.length === 0) return Promise.reject('无效的 msg 内容')

	return new Promise((resolve, reject) => {
		const defaultOptions: MsgOptions = {
			title: '',
			content: '',
			pic: '',
			picPosition: 'left',
			position: 'center',
			height: 'auto',
			confirmButtonText: '确定',
			cancelButtonText: '取消',
			global: false,
			show: false
		}

		let options: MsgOptions = { ...defaultOptions }

		// 处理参数
		if (args.length === 1) {
			if (typeof args[0] === 'object') {
				options = { ...defaultOptions, ...args[0] }
			} else {
				options = { ...defaultOptions, content: String(args[0]) }
			}
		} else if (args.length >= 2) {
			if (typeof args[0] === 'string') {
				switch (args[0]) {
					case 'success':
						options.pic = 'success'
						break
					case 'error':
						options.pic = 'error'
						break
					case 'help':
						options.pic = 'help'
						break
					case 'loading':
						options.pic = 'loading'
						break
					case 'info':
						options.pic = 'info'
						break
					default:
						options.pic = 'success'
						break
				}
			}

			if (typeof args[1] === 'object') {
				options = { ...options, ...args[1] }
			}
		}

		// 创建容器
		const body = document.body
		const uuid = `msg-${generateUUID()}`
		const container = document.createElement('div')
		container.setAttribute('id', uuid)
		body.appendChild(container)

		// 处理VNode类型的content和title，避免同时作为props和slots传递
		const isTitleVNode = isVNode(options.title)
		const isDetailVNode = isVNode(options.detail)

		const props = {
			...options,
			...(isTitleVNode ? { title: undefined } : {}),
			...(isDetailVNode ? { detail: undefined } : {})
		}

		// 创建VNode并渲染
		const vnode = h(
			Msg,
			{
				...props,
				onClose: () => {
					setTimeout(() => {
						render(null, container)
						removeElement(container)
						reject(false)
					}, 200)
				},
				onConfirm: () => {
					render(null, container)
					removeElement(container)
					resolve(true)
				}
			},
			{
				title: isTitleVNode ? options.title : null,
				detail: isDetailVNode ? options.detail : null
			}
		)

		// 设置应用上下文
		if (app && app._context) {
			vnode.appContext = app._context
		}

		// 渲染组件到容器
		render(vnode, container)

		// 等待DOM挂载完成后调用open方法
		const componentInstance = vnode.component
		requestAnimationFrame(() => {
			if (componentInstance && componentInstance.exposed && typeof componentInstance.exposed.open === 'function') {
				componentInstance.exposed.open()
			}
		})
	})
}

// 创建$msg函数工厂
function createMsgFunction(app: any) {
	const $msg = function (...args: any[]): Promise<boolean> {
		app = app ? app : ToMsg._context
		return createMsgInstance(app, ...args)
	}

	// 添加便捷方法
	$msg.success = function (options: MsgOptions): Promise<boolean> {
		// 成功提示默认不需要取消按钮
		if (typeof options === 'object') {
			options.cancelButtonText = ''
		} else {
			options = { cancelButtonText: '' }
		}
		return $msg('success', options)
	}

	$msg.error = function (options: MsgOptions): Promise<boolean> {
		return $msg('error', options)
	}

	$msg.help = function (options: MsgOptions): Promise<boolean> {
		return $msg('help', options)
	}

	$msg.loading = function (options: MsgOptions): Promise<boolean> {
		return $msg('loading', options)
	}

	$msg.info = function (options: MsgOptions): Promise<boolean> {
		return $msg('info', options)
	}

	$msg.confirm = function (options: MsgOptions): Promise<boolean> {
		return $msg('help', options)
	}

	return $msg
}

export const $msg = createMsgFunction(null)

const originInstall = ToMsg.install

ToMsg.install = app => {
	originInstall(app)
	ToMsg._context = app
	const appMsg = createMsgFunction(app)
	app.config.globalProperties.$msg = appMsg
}

export default $msg

export { ToMsg }
